/**
 * Central UI string table + simple lookup helper.
 *
 * All user-facing text in HUD widgets, panels, and floating toasts goes
 * through `t(key, params?)`. Each entry is a template string with `{name}`
 * placeholders that `t()` substitutes from `params`. Today the dictionary
 * is English-only — when a translation layer is added, swap the active
 * table (e.g. `STRINGS = locale === 'fr' ? FR : EN`) and the rest of the
 * UI keeps working unchanged.
 *
 * Why a flat key namespace: keys are short, debuggable, and grep-able.
 * `truck.dispatch.send` is easier to track than nested object paths.
 *
 * Sim-side hint events still emit raw English text (Sim.bus.emit({ type:
 * 'hint', text: 'Storage full…' })) — that's intentional, the sim doesn't
 * know about UI locale. When/if we localize those, the HintToast resolver
 * can detect known-message-shaped strings and look them up by hash, or
 * we switch the sim to emit keys and resolve in the UI layer.
 */

type Params = Record<string, string | number>;

const EN: Record<string, string> = {
  // ── HintToast / global ───────────────────────────────────────────
  'hint.intro': 'Click buildings to open them. Q/E rotate, R reset, X demolishes the hovered building.',
  'hint.leveledUp': 'Level {level}! New unlocks!',

  // ── Objective widget ────────────────────────────────────────────
  'goal.storageFull':   'Storage full — load the truck or sell in town.',
  'goal.energyLow':     'Energy low — rest until it recovers, or sell goods to keep going.',
  'goal.truckLoaded':   'Truck loaded — press "Send truck to town" to ship.',
  'goal.matureCrops':   'Click golden crops with the sickle to harvest.',
  'goal.emptyField':    'Click an empty brown field to plant a seed.',
  'goal.noSeeds':       'Buy more seeds at the truck panel, or wait for delivery.',
  'goal.forest':        'Chop a tree with the axe to gather wood.',
  'goal.grass':         'Till some grass with the hoe to start a field.',
  'goal.idle':          'Explore — plant, harvest, build, expand.',

  // ── TruckPanel ──────────────────────────────────────────────────
  'truck.status.idle':      'Truck parked at depot — ready to load + dispatch',
  'truck.status.loading':   'Truck loading — press Send to dispatch',
  'truck.status.departed':  'Heading to town — {secs}s to delivery',
  'truck.status.returning': 'Returning with {coins} coins — {secs}s away',
  'truck.tip.intro':        'Drop items on the platform any time. The truck waits parked until you press Send.',
  'truck.tip.empty':        'Nothing to ship yet — harvest, mill, chop, or forage to fill the truck.',
  'truck.row.storage':      '{n} in storage',
  'truck.row.queued':       '{n} queued',
  'truck.row.onTruck':      '{n} on truck',
  'truck.btn.loadAll':      'Load all',
  'truck.btn.loadOne':      '+1',
  'truck.btn.recall':       'Take back',
  'truck.btn.recall.title': 'Move items back from the depot platform to your storage.',
  'truck.btn.load.tooltipAway': 'Truck is away — items will queue on the depot platform and load on return.',
  'truck.queue.label':      'Queued for next pickup',
  'truck.queue.value':      '{n} {items} · {coins}c',
  'truck.cargo.label':      'On truck',
  'truck.cargo.empty':      'empty, ready to load',
  'truck.cargo.value':      '{n} / {cap} · {coins}c',
  'truck.seedErrand.label': 'Seed errand',
  'truck.payout.label':     'Estimated payout',
  'truck.payout.value':     '{coins}c',
  'truck.payout.valueQueue':'{coins}c (truck + queue)',
  'truck.payout.title.truckOnly': 'Truck cargo only.',
  'truck.payout.title.withQueue': 'Includes {n} {items} from the queue.',
  'truck.send.btn':         'Send truck to town (round-trip {secs}s)',
  'truck.section.buySeeds': 'Buy seeds',
  'truck.section.buySeedsTip': 'Order seeds — the truck fetches them on the next round-trip.',
  'truck.section.buyStone': 'Buy stone',
  'truck.section.buyStoneTip': 'Order stone — the truck hauls it back on the next round-trip.',
  'truck.seed.row.stock':   '{n} in stock',
  'truck.seed.btn.qty':     '+{qty} ({cost}c)',
  'truck.seed.price':       '@ {price}c each',
  'truck.stone.row.stock':  '{n} in stock',
  'truck.stone.btn.qty':    '+{qty} ({cost}c)',
  'truck.stone.price':      '@ {price}c each',
  'truck.stoneErrand.label':'Stone errand',
  'truck.cargo.price':      '@ {price}c',

  // ── ZonesPanel ──────────────────────────────────────────────────
  'zones.title':            'Zones',
  'zones.tip':              'Click a plot card to dive in. Use "New zone" to paint a fresh plot, or click a card\'s "Edit" to add/remove tiles by clicking the world.',
  'zones.empty':            'No plots yet — click "New zone" and paint tiles to create one.',
  'zones.btn.new':          '+ New zone',
  'zones.btn.adopt':        'Adopt',
  'zones.btn.cancel':       'Cancel',
  'zones.btn.done':         'Done',
  'zones.btn.close':        'Close',
  'zones.btn.edit':         'Edit tiles',
  'zones.btn.delete':       'Delete',
  'zones.btn.rename':       'Rename',
  'zones.card.tiles':       '{n} tile{s}',
  'zones.card.editing':     'Editing — click world tiles to add or remove',
  'zones.new.title':        'New zone',
  'zones.new.tip':          'Click tiles to add them. "Adopt" saves the plot.',
  'zones.new.namePlaceholder': 'Plot {n}',
  'zones.confirmDelete':    'Delete plot "{name}"? All work on its tiles will move back to free.',

  // ── PlotConfirmPanel ────────────────────────────────────────────
  'plot.confirm.title':     'New Plot',
  'plot.confirm.tip.empty': 'Drag across the map to paint tiles into your plot. Skip buildings and the road.',
  'plot.confirm.tip.named': 'A plot is just a NAMED grouping of tiles. Tools work everywhere; the plot view zooms in for focused work + tracks progress. You can return any time.',
  'plot.confirm.tiles':     'Tiles in plot',
  'plot.confirm.section':   'Selection',
  'plot.confirm.nameLabel': 'Name this plot',
  'plot.confirm.namePlaceholder': 'Plot {n}',
  'plot.confirm.btn.adopt': 'Adopt + enter plot',
  'plot.confirm.btn.cancel': 'Cancel',

  // ── BuildPanel ──────────────────────────────────────────────────
  'building.granary.label': 'Granary',
  'building.granary.desc':  '+50 storage · 2 stages',
  'building.mill.label':    'Windmill',
  'building.mill.desc':     '5 grain → flour · 3 stages',
  'building.mailbox.label': 'Mailbox',
  'building.depot.label':   'Truck Depot',

  // ── Hotbar / gardening popover ──────────────────────────────────
  'hotbar.popover.gardening': 'Gardening',
  'hotbar.popover.seeds':     'Seeds',
  'hotbar.popover.build':     'Build',
  'hotbar.slot.plot':         'Plot',
  'hotbar.slot.hands':        'Hands',
  'hotbar.slot.axe':          'Axe',
  'hotbar.slot.pickaxe':      'Pickaxe',
  'hotbar.slot.gardening':    'Gardening',
  'hotbar.slot.build':        'Build',
  'hotbar.tool.hoe':          'Hoe',
  'hotbar.tool.water':        'Water',
  'hotbar.tool.plant':        'Plant',
  'hotbar.tool.scythe':       'Sickle',

  // ── StorageBar sections ─────────────────────────────────────────
  'storage.section.allCrops':   'All crops',
  'storage.section.breakdown':  'Breakdown',
  'storage.section.seeds':      'Seeds',
  'storage.section.milled':     'Milled',
  'storage.section.materials':  'Materials',
  'storage.section.foraged':    'Foraged',
  'storage.chip.wheat':         'Wheat',
  'storage.chip.corn':          'Corn',
  'storage.chip.carrot':        'Carrot',
  'storage.chip.seedWheat':     'Wheat seeds',
  'storage.chip.seedCorn':      'Corn seeds',
  'storage.chip.seedCarrot':    'Carrot seeds',
  'storage.chip.flour':         'Flour',
  'storage.chip.cornFlour':     'Corn flour',
  'storage.chip.wood':          'Wood',
  'storage.chip.stone':         'Stone',
  'storage.chip.acorn':         'Acorns',
  'storage.chip.mushroom':      'Mushrooms',
  'storage.chip.berry':         'Berries',

  // ── InfoCard (plot-aggregate mode) ──────────────────────────────
  'infocard.plot.title':   'Plot overview',
  'infocard.plot.tiles':   'Tiles: {n}',
  'infocard.plot.weeds':   'Weeds: {n}',
  'infocard.plot.tilled':  'Tilled: {pct}% ({cur}/{total})',
  'infocard.plot.watered': 'Watered: {pct}% ({cur}/{total})',
  'infocard.plot.seeded':  'Seeded: {pct}% ({cur}/{total})',

  // ── FlyToHud labels ─────────────────────────────────────────────
  'fly.acorns':         '+{n} acorn{s}',
  'fly.mushrooms':      '+{n} mushroom{s}',
  'fly.berries':        '+{n} berr{ies}',
  'fly.wood':           '+{n} wood',
  'fly.stone':          '+{n} stone',
  'fly.flour':          '+{n} flour',
  'fly.cornFlour':      '+{n} corn flour',
  'fly.seedOrdered':    'Ordered +{qty} {seed}',
  'fly.seedDelivered':  '+{qty} {seed}',
  'fly.forageMushroom': '+{n} mushroom',
  'fly.forageBerry':    '+{n} berry',
};

const STRINGS: Record<string, string> = EN;

/** Resolve `key` and interpolate `{name}` placeholders with values from
 *  `params`. Falls back to the key itself if not found so missing entries
 *  are visible in the UI rather than silently empty. */
export function t(key: string, params?: Params): string {
  const tpl = STRINGS[key] ?? key;
  if (!params) return tpl;
  return tpl.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined ? `{${name}}` : String(v);
  });
}

/** Tiny pluralizer for English: returns the suffix that should follow
 *  a word given a count. e.g. plural(1, 's') = '', plural(2, 's') = 's'.
 *  For irregular plurals (`berry → berries`) caller passes the suffix to
 *  swap: plural(n, 'ies', 'y') returns 'ies' for n!=1 and 'y' for n==1. */
export function plural(n: number, many = 's', one = ''): string {
  return n === 1 ? one : many;
}
